/**
 * What the league gets pinged about, and the exact words it gets pinged with.
 *
 * Kept pure and in core for one reason: deciding "did something notify-worthy
 * happen between these two snapshots of a season?" is a diff, and a diff is the
 * kind of thing that quietly notifies twelve people at 3am when it's wrong. It
 * lives here so it can be tested without Firestore, a push service, or a phone.
 *
 * The API owns delivery (subscriptions, VAPID, fan-out); this file owns the
 * decision and the copy.
 */
/**
 * Everything on by default, and deliberately so: a member who has never opened
 * the settings has no stored document, and the fan-out must read that as "send
 * me things", not "skip". The same rule covers adding a fifth event type later —
 * it arrives on for everyone rather than silently off for anyone with an old
 * document.
 */
export const DEFAULT_NOTIFY_PREFS = {
    newPost: true,
    reaction: true,
    recap: true,
    results: true,
    tipSheet: true,
    lineupAlert: true,
};
/** Stored prefs layered over the defaults, so a partial document is safe. */
export function mergeNotifyPrefs(stored) {
    if (!stored)
        return { ...DEFAULT_NOTIFY_PREFS };
    const out = { ...DEFAULT_NOTIFY_PREFS };
    for (const key of Object.keys(DEFAULT_NOTIFY_PREFS)) {
        if (typeof stored[key] === 'boolean')
            out[key] = stored[key];
    }
    return out;
}
/** The toggle list the Profile page renders. One source of truth for both ends. */
export const NOTIFY_EVENTS = [
    {
        id: 'newPost',
        label: 'Trash talk',
        description: 'Somebody posts on the War Room or a weekly board.',
    },
    {
        id: 'reaction',
        label: 'Reactions to you',
        description: 'Somebody reacts to something you posted.',
    },
    { id: 'recap', label: 'Roasts', description: 'The weekly AI roast gets published.' },
    {
        id: 'results',
        label: 'Money and results',
        description: 'Weekly pots, eliminations, and season awards changing hands.',
    },
    {
        id: 'tipSheet',
        label: 'Tip Sheet',
        description: 'The weekly lineup advice and power rankings get published.',
    },
    {
        id: 'lineupAlert',
        label: 'Lineup alerts',
        description: 'A starter of yours is out, on IR, or on bye before kickoff.',
    },
];
/**
 * Notification bodies are a headline, not an essay: Android collapses around
 * two lines and iOS around four. Trim on a word boundary when one is close.
 */
export function truncateForPush(text, max = 110) {
    const clean = text.replace(/\s+/g, ' ').trim();
    if ([...clean].length <= max)
        return clean;
    const cut = [...clean].slice(0, max).join('');
    const lastSpace = cut.lastIndexOf(' ');
    return `${(lastSpace > max * 0.6 ? cut.slice(0, lastSpace) : cut).trimEnd()}…`;
}
function ownerName(bundle, ownerId) {
    return bundle.data.owners.find((o) => o.id === ownerId)?.name ?? 'Somebody';
}
/** "A", "A and B", "A, B and C" — ties are real money here, so name everyone. */
function nameList(bundle, ownerIds) {
    const names = ownerIds.map((id) => ownerName(bundle, id));
    if (names.length <= 1)
        return names[0] ?? 'Somebody';
    return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}
function findAward(bundle, id, week) {
    return bundle.money.awards.find((a) => a.id === id && (week === undefined || a.week === week));
}
/**
 * Season-long awards whose holder changing hands is worth an alert.
 *
 * Read off the season's own award list rather than hardcoded here: which pots
 * are worth interrupting people for is a league's taste, and it belongs next to
 * the pot it describes.
 */
function leadAwards(bundle) {
    return (bundle.money.config?.awards ?? [])
        .filter((a) => !a.perWeek && a.notify?.onLeadChange)
        .map((a) => ({ id: a.id, ...a.notify.onLeadChange }));
}
/** Awards that pay out once per week. */
function weeklyAwards(bundle) {
    return (bundle.money.config?.awards ?? []).filter((a) => a.perWeek);
}
/**
 * How many missed weeks a single sync will announce. A cron outage or a quiet
 * September can leave `latestCompletedWeek` jumping several weeks at once;
 * catching up matters (nobody wants a week's $30 to go unannounced), but
 * catching up on the whole season at once does not.
 */
const MAX_CATCHUP_WEEKS = 3;
/**
 * Everything notify-worthy about the weeks that closed between two syncs.
 *
 * The hard gate is `nextWeek > prevWeek` — a week actually going final. Nothing
 * else can produce an event here, and that single rule does most of the work:
 *
 *  - it makes the four-minute live-scoring re-syncs silent all Sunday,
 *  - it makes a re-sync of an unchanged season silent,
 *  - and it makes the cron's backfill pass over old seasons silent, which
 *    matters most: `syncAllSeasons` walks every season, and without this the
 *    first run after a deploy would replay four finished seasons at once.
 *
 * Note this reads only the NEW snapshot: what happened in week 4 is a fact
 * about week 4, not a difference between two syncs. That keeps it honest when
 * ESPN revises a final score days later — the keys are per week, never per
 * winner, so a correction quietly fixes the board instead of contradicting a
 * notification people already read.
 */
export function resultEvents(next, prevWeek, nextWeek) {
    if (!(nextWeek > prevWeek))
        return [];
    const season = next.data.season;
    const events = [];
    const from = Math.max(prevWeek + 1, nextWeek - MAX_CATCHUP_WEEKS + 1);
    for (let week = from; week <= nextWeek; week += 1) {
        // The weekly pot. Found by award rather than by week alone: a league can
        // pay more than one thing every week, and "the award for this week" would
        // then be ambiguous.
        for (const rule of weeklyAwards(next)) {
            const weekly = findAward(next, rule.id, week);
            if (!weekly || weekly.status !== 'settled' || weekly.winnerIds.length === 0)
                continue;
            const who = nameList(next, weekly.winnerIds);
            const took = weekly.winnerIds.length > 1
                ? `${who} split the $${weekly.amount}`
                : `${who} took the $${weekly.amount}`;
            const tag = `${rule.id}-${season}-${week}`.slice(0, 32);
            events.push({
                key: `${season}:w${week}:${rule.id}`,
                topic: tag,
                payload: {
                    title: `Week ${week} is in the books`,
                    body: weekly.detail ? `${took} with ${weekly.detail}.` : `${took}.`,
                    // The week, not whatever week is current when this is tapped: home
                    // follows ?week, and a notification opened on Thursday is about
                    // Sunday. The catch-up path can emit several weeks at once, which
                    // without this would all open the same board.
                    url: `/?week=${week}`,
                    tag,
                },
            });
        }
        // Survivor: whoever went out that week. The board tracks eliminations for
        // every league; only one that pays for it wants to be told.
        const paysSurvivor = (next.money.config?.awards ?? []).some((a) => a.compute?.kind === 'survivor');
        const knockedOut = paysSurvivor
            ? next.money.survivor.eliminations.filter((e) => e.week === week)
            : [];
        if (knockedOut.length > 0) {
            const who = nameList(next, knockedOut.map((e) => e.ownerId));
            const remaining = Math.min(...knockedOut.map((e) => e.remaining));
            // Only the elimination that actually leaves one manager standing crowns a
            // winner. Catching up on missed weeks walks earlier eliminations too, and
            // the snapshot already knows who won — without this, week 4's alert would
            // announce a champion three weeks early.
            const winnerId = remaining <= 1 ? next.money.survivor.winnerId : null;
            const body = winnerId
                ? `${who} is out. ${ownerName(next, winnerId)} survives and takes the pool.`
                : `${who} ${knockedOut.length > 1 ? 'are' : 'is'} out with ${knockedOut
                    .map((e) => e.score.toFixed(1))
                    .join(' and ')}. ${remaining} still alive.`;
            events.push({
                key: `${season}:w${week}:survivor`,
                topic: `surv-${season}-${week}`,
                payload: {
                    title: knockedOut.length > 1 ? 'Survivor: two down' : 'Survivor: one down',
                    body,
                    url: `/?week=${week}`,
                    tag: `surv-${season}-${week}`,
                },
            });
        }
    }
    return events;
}
/**
 * Season-long awards that have changed hands since we last said so.
 *
 * These deliberately do NOT diff two sync snapshots. Goon is computed from live
 * scores, so by the time a week goes final the four-minute live sync has already
 * written the new leader into the previous snapshot — a diff would compare two
 * identical values and the notification would never fire. What matters is not
 * "did this change since the last sync" but "did this change since the last time
 * we told anyone", so the caller keeps the announced holders and passes them
 * back in.
 *
 * Call this ONLY when a week has gone final. Goon moves on live scores, so on a
 * Sunday afternoon the lead can change hands several times between kickoff and
 * the late window; asked on every four-minute sync, this would narrate all of it.
 *
 * Returns the holders to persist alongside the events, so a send that is skipped
 * (or a season with no data) can't quietly advance the record.
 */
export function leadEvents(next, week, announced) {
    const season = next.data.season;
    const events = [];
    const holders = { ...(announced ?? {}) };
    for (const spec of leadAwards(next)) {
        const award = findAward(next, spec.id);
        if (!award || award.winnerIds.length === 0)
            continue;
        const current = award.winnerIds.join(',');
        const last = announced?.[spec.id];
        holders[spec.id] = current;
        // No record yet means this is the first time we've looked, not a change of
        // hands: whoever leads in week 1 hasn't taken anything from anyone.
        if (last === undefined || last === current)
            continue;
        events.push({
            // The week is part of the key, not just the holder: a lead that goes
            // A -> B -> A -> B over four weeks is four real changes, and keying on the
            // holder alone would silently swallow the second move to B. Two syncs
            // racing within one week still dedupe, which is the point.
            key: `${season}:w${week}:${spec.id}:${current}`,
            topic: `${spec.id}-${season}`.slice(0, 32),
            payload: {
                title: spec.title,
                body: award.detail
                    ? `${nameList(next, award.winnerIds)} ${spec.verb} — ${award.detail}.`
                    : `${nameList(next, award.winnerIds)} ${spec.verb}.`,
                // The side pots, not the ledger: this alert is a POT changing hands,
                // and the pot's own ticket is on the standings page. '/' used to mean
                // the money board and now means the weekly home page, so a league that
                // leaves this out was being sent somewhere that says nothing about the
                // award it was just told about.
                url: spec.url ?? '/standings',
                tag: `${spec.id}-${season}`,
            },
        });
    }
    return { events, holders };
}
//# sourceMappingURL=notify.js.map